import React, { useCallback, useEffect, useState } from 'react';
import {
    Box,
    Button,
    EmptyStateActions,
    FlexBox,
    FormField,
    FormLabel,
    SearchInput,
    SelectionSet,
    Spinner,
    Typography,
} from '@vp/swan';
import { useIdentityContext } from '@99designs/design-services-common';
import { Pagination } from '@99designs/design-services-common';
import { __ } from '@99designs/i18n';
import { useBriefFormContext } from '../../../../BriefContext/BriefFormContext';
import { ClientConfiguredFieldProps } from '../types';
import { ReferenceProjectsEmptyState } from './ReferenceProjectsEmptyState';
import { WorkEntityItem } from './WorkEntityItem';
import useFormatDistance from './useFormatDistance';
import useUpdateReferenceProjects from './useUpdateReferenceProjects';
import { useWorkEntities } from './useWorkEntities';

type SelectedState = Record<string, boolean>;

function stateToSelectedIds(state: SelectedState): string[] {
    const selectedIds: string[] = [];
    Object.entries(state).forEach(([key, value]) => {
        if (value) {
            selectedIds.push(key);
        }
    });

    return selectedIds;
}

function selectedIdsToState(selected: string[]): Record<string, boolean> {
    const state: SelectedState = {};
    selected.forEach((id) => {
        state[id] = true;
    });
    return state;
}

export function valueToWorkIds(value: string): string[] {
    if (!value) {
        return [];
    }
    return value.split(',');
}

function valueToSelectedState(value: string): SelectedState {
    const workIds = valueToWorkIds(value);
    return selectedIdsToState(workIds);
}

function totalSelected(state: SelectedState): number {
    return Object.values(state).reduce((total, isSelected) => {
        if (isSelected) {
            return total + 1;
        }
        return total;
    }, 0);
}

const INITIAL_PAGE = 1;
const MAX_SELECTED = 5;

export function Input(props: ClientConfiguredFieldProps) {
    const { SignIn, isSignedIn } = useIdentityContext();

    if (!isSignedIn) {
        return (
            <ReferenceProjectsEmptyState
                illustrationType="myProjects"
                title={__('Please sign in to add a project', {
                    _context: 'This is the title shown when the user is not signed in',
                })}
            >
                <EmptyStateActions>
                    <Button skin="secondary" onClick={() => SignIn()}>
                        {__('Sign in')}
                    </Button>
                </EmptyStateActions>
            </ReferenceProjectsEmptyState>
        );
    }
    return <InputWithoutSignIn {...props} />;
}

export function InputWithoutSignIn({
    id,
    clientConfiguredValue: initialValue,
    register,
    setValue,
}: ClientConfiguredFieldProps) {
    const [currentPage, setCurrentPage] = useState(INITIAL_PAGE);
    const [searchTerm, setSearchTerm] = useState('');
    const setSearchTermAndResetPage = useCallback(
        (term: string) => {
            setCurrentPage(INITIAL_PAGE);
            setSearchTerm(term);
        },
        [setSearchTerm, setCurrentPage]
    );

    const { loading, error, pastWorkEntities, totalPages, totalResults, search, allTotalResults } =
        useWorkEntities(currentPage, searchTerm);

    const { briefId } = useBriefFormContext();

    const updateMutation = useUpdateReferenceProjects(id, briefId, initialValue);

    const [selectedState, setSelectedState] = useState(valueToSelectedState(initialValue));

    const formatDistance = useFormatDistance();

    /**
     * SelectionSet requires all options to be represented in the selectedValues prop.
     */
    pastWorkEntities?.forEach((e) => {
        if (!(e.workId in selectedState)) {
            selectedState[e.workId] = false;
        }
    });

    const totalSelectedCount = totalSelected(selectedState);

    useEffect(() => {
        register(id);
    }, []); // eslint-disable-line react-hooks/exhaustive-deps

    useEffect(() => {
        const selectedIds = stateToSelectedIds(selectedState);
        const value = selectedIds.join(',');
        setValue(id, value);
        updateMutation(value);
    }, [updateMutation, selectedState, setValue, id]);

    const workEntitiesLoaded = !loading && !error && pastWorkEntities;

    if (workEntitiesLoaded && allTotalResults === 0) {
        return (
            <ReferenceProjectsEmptyState
                illustrationType="myProjects"
                title={__('No projects to reference', {
                    _context:
                        'This is the title shown when there are no past projects to reference',
                })}
                description={__(
                    "Past Vistaprint projects will appear here once you've created them.",
                    {
                        _context:
                            'This is the subtitle shown when there are no past projects to reference',
                    }
                )}
            />
        );
    }

    return (
        <Box>
            <Typography mb={4} fontSkin="body-small">
                {selectedText(totalSelectedCount)}
            </Typography>
            <FormField mb={6}>
                <FormLabel visuallyHidden>
                    {__('Search projects to reference', {
                        _context:
                            'Visually hidden label for the search input used to find past projects to reference in the brief',
                    })}
                </FormLabel>
                <SearchInput
                    placeholder={__('Search projects', {
                        _context:
                            'Placeholder text for the search input used to find past projects to reference',
                    })}
                    value={searchTerm}
                    onChange={(e) => setSearchTermAndResetPage(e.target.value)}
                    onClear={() => setSearchTermAndResetPage('')}
                    accessibleTextForClearButton={__('Clear search input', {
                        _context:
                            'Accessible text for the clear button inside the search input used for reference projects',
                    })}
                    accessibleTextForSearchButton={__('Submit search', {
                        _context:
                            'Accessible text for the search/submit button inside the search input used for reference projects',
                    })}
                    onSearchSubmit={async (e) => {
                        e.preventDefault();
                        /**
                         * Search immediately on submit rather than waiting for debounce
                         */
                        await search(searchTerm, INITIAL_PAGE);
                    }}
                    data-testid="reference-projects-search-input"
                />
            </FormField>
            <Box
                style={{
                    minHeight: '530px', // To prevent layout shift when loading different size result
                }}
            >
                {loading && (
                    <Box my={10} textAlign="center" data-testid="loading-spinner">
                        <Spinner accessibleText={__('Loading...')} />
                    </Box>
                )}
                {error && (
                    <ReferenceProjectsEmptyState
                        illustrationType="error"
                        title={__("Can't load projects", {
                            _context:
                                'This is the title shown when there is an error loading projects',
                        })}
                        description={__(
                            'We’re having trouble loading your projects. Please try again later or continue without adding projects',
                            {
                                _context:
                                    'This is the subtitle shown when there is an error loading projects',
                            }
                        )}
                    />
                )}
                {workEntitiesLoaded && (
                    <FlexBox flexDirection={'column'} gap={'between-actions'}>
                        <Typography fontSkin="body-standard-bold">
                            {resultCountText(totalResults, searchTerm)}
                        </Typography>
                        <SelectionSet variant="multi-select" selectedValues={selectedState}>
                            {pastWorkEntities.map((workEntity) => {
                                const isSelected = !!selectedState[workEntity.workId];

                                return (
                                    <WorkEntityItem
                                        id={workEntity.workId}
                                        key={workEntity.workId}
                                        selectedValue={isSelected}
                                        disabled={totalSelectedCount >= MAX_SELECTED && !isSelected}
                                        onChange={() => {
                                            if (totalSelectedCount > MAX_SELECTED && !isSelected) {
                                                return;
                                            }
                                            setSelectedState({
                                                ...selectedState,
                                                [workEntity.workId]: !isSelected,
                                            });
                                        }}
                                        title={workEntity.workName}
                                        editedAt={__(`Edited {{formattedDate}}`, {
                                            formattedDate: formatDistance(
                                                workEntity.modified,
                                                new Date(),
                                                {
                                                    addSuffix: true,
                                                }
                                            ),
                                            _context:
                                                "Displays how long ago a project was edited, e.g., 'Edited 3 days ago'. The formattedDate variable is replaced with a human-readable time difference.",
                                        })}
                                        previewUrl={workEntity.views[0]?._links.image.href || ''}
                                    />
                                );
                            })}
                        </SelectionSet>
                        <FlexBox justifyContent={'center'} mt={'4'}>
                            {!loading && totalPages > 1 && (
                                <Pagination
                                    currentPage={currentPage}
                                    totalPages={totalPages}
                                    onChange={async (page) => {
                                        await search(searchTerm, page);
                                        setCurrentPage(page);
                                    }}
                                />
                            )}
                        </FlexBox>
                    </FlexBox>
                )}
            </Box>
        </Box>
    );
}

/**
 * Generates the result count text based on the total results and search term.
 * Handles singular/plural forms and includes the search term if applicable.
 */
function resultCountText(totalResults: number, searchTerm: string): string {
    if (searchTerm) {
        if (totalResults === 1) {
            return __('1 result for "{{searchTerm}}"', {
                searchTerm,
                _context:
                    'Result count label shown above the project list after searching; singular form; searchTerm is the user-entered query',
            });
        }
        return __('{{totalResults}} results for "{{searchTerm}}"', {
            totalResults,
            searchTerm,
            _context:
                'Result count label shown above the project list after searching; plural form; totalResults is a number and searchTerm is the user-entered query',
        });
    }

    if (totalResults === 1) {
        return __('1 project', {
            _context:
                'Result count label shown above the project list when not searching; singular form',
        });
    }

    return __('{{totalResults}} projects', {
        totalResults,
        _context:
            'Result count label shown above the project list when not searching; plural form; totalResults is a number',
    });
}

function selectedText(selectedCount: number): string {
    if (selectedCount === 1) {
        return __('1 project added', {
            _context:
                'Summary text above the project list indicating how many projects the user has selected; singular form',
        });
    }
    if (selectedCount < MAX_SELECTED) {
        return __('{{selectedCount}} projects added', {
            selectedCount,
            _context:
                'Summary text above the project list indicating how many projects the user has selected; plural form; selectedCount is a number',
        });
    }
    return __('{{selectedCount}} projects added (Maximum reached)', {
        selectedCount,
        _context:
            'Summary text above the project list indicating how many projects the user has selected; shown when the maximum number of selected projects has been reached; selectedCount is a number',
    });
}
